// src/services/api.service.js
import CryptoJS from 'crypto-js';
import { CONTENT_ENDPOINTS, USER_ENDPOINTS, STREAM_ENDPOINTS, DELETE_ENDPOINTS } from '../utils/constants';

const ENCRYPTION_KEY = 'k1aY2nI0s4a8Y2nI0s4a8mi0109sdrtw2';

export const fetchStorageData = async (token) => {
  try {
    const response = await fetch(CONTENT_ENDPOINTS.ALL_BUCKETS_SIZE, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed');
      }
      throw new Error(`Storage data fetch failed: ${response.status}`);
    }

    const data = await response.json();

    if (!data || !Array.isArray(data.bucketSizes)) {
      return { bucketSizes: [] };
    }

    return data;
  } catch (error) {
    console.error('Error fetching storage data:', error);
    return { bucketSizes: [] };
  }
};

export const fetchBucketItemCount = async (bucket, token) => {
  try {
    const response = await fetch(CONTENT_ENDPOINTS.BUCKET_CONTENT_PAGE, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        bucket: bucket,
        page: 1,
        limit: 1
      }),
    });

    if (!response.ok) {
      console.warn(`${bucket} count fetch failed: ${response.status}`);
      return { bucket, data: { totalItems: 0 } };
    }

    const data = await response.json();
    return { bucket, data };
  } catch (error) {
    console.error(`Error fetching ${bucket} count:`, error);
    return { bucket, data: { totalItems: 0 } };
  }
};

export const fetchAlerts = async (email, token) => {
  try {
    const response = await fetch(
      `${USER_ENDPOINTS.GET_ALERTS}?email=${encodeURIComponent(email)}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) throw new Error("Failed to fetch alerts");

    return await response.json();
  } catch (error) {
    console.error("Error fetching alerts:", error);
    throw error;
  }
};

export const clearAlert = async (alertId, email, token) => {
  try {
    const response = await fetch(
      USER_ENDPOINTS.CLEAR_ALERT.replace(':id', alertId),
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      }
    );

    if (!response.ok) throw new Error("Failed to clear alert");

    return await response.json();
  } catch (error) {
    console.error("Error clearing alert:", error);
    throw error;
  }
};

export const clearAllAlerts = async (email, token) => {
  try {
    const response = await fetch(USER_ENDPOINTS.CLEAR_ALL_ALERTS, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) throw new Error("Failed to clear all alerts");

    return await response.json();
  } catch (error) {
    console.error("Error clearing all alerts:", error);
    throw error;
  }
};

export const CACHE_NAME = 'bucket-content-cache-v4';

export const clearBucketContentCache = async () => {
  try {
    const cacheKeys = await caches.keys();
    const keysToDelete = cacheKeys.filter(key => key.startsWith(CACHE_NAME));
    await Promise.all(keysToDelete.map(key => caches.delete(key)));

    // Also clear sessionStorage cache if being used
    Object.keys(sessionStorage).forEach(key => {
      if (key.startsWith('cache-')) {
        sessionStorage.removeItem(key);
      }
    });

    return true;
  } catch (error) {
    console.error('Error clearing cache:', error);
    return false;
  }
};

export const setCachedData = async (key, data, expirationMinutes = 30) => {
  try {
    const cacheItem = {
      data,
      expires: Date.now() + (expirationMinutes * 60 * 1000)
    };
    sessionStorage.setItem(`cache-${key}`, JSON.stringify(cacheItem));
    return true;
  } catch (error) {
    console.error('Error setting cache:', error);
    return false;
  }
};

export const getCachedData = async (key) => {
  try {
    const cacheItem = sessionStorage.getItem(`cache-${key}`);
    if (!cacheItem) return null;

    const { data, expires } = JSON.parse(cacheItem);
    if (Date.now() > expires) {
      sessionStorage.removeItem(`cache-${key}`);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error getting cache:', error);
    return null;
  }
};

// Encryption/decryption utilities
export const encryptData = (data) => {
  return CryptoJS.AES.encrypt(
    typeof data === 'string' ? data : JSON.stringify(data),
    ENCRYPTION_KEY
  ).toString();
};

export const decryptData = (ciphertext) => {
  try {
    const bytes = CryptoJS.AES.decrypt(ciphertext, ENCRYPTION_KEY);
    const decryptedText = bytes.toString(CryptoJS.enc.Utf8);

    // Try to parse as JSON, return as string if it fails
    try {
      return JSON.parse(decryptedText);
    } catch (e) {
      return decryptedText;
    }
  } catch (error) {
    console.error('Decryption error:', error);
    return null;
  }
};

// Get user credentials from session storage
export const getUserCredentials = () => {
  const userDetails = sessionStorage.getItem('userDetails');
  if (!userDetails) return null;

  const userDetailsObj = JSON.parse(userDetails);
  const token = sessionStorage.getItem('userToken');

  return {
    email: userDetailsObj.Email,
    token
  };
};

// Generic bucket content fetcher
export const fetchBucketContent = async (bucketName, token, forceClearCache = false) => {
  try {
    if (!token) {
      console.warn('No token provided to fetchBucketContent');
      // Try to get from session storage as fallback
      const credentials = getUserCredentials();
      if (!credentials) {
        throw new Error('No authentication information found');
      }
      token = credentials.token;
    }

    // Check cache first (unless forced to clear)
    const cacheKey = `bucket-${bucketName}`;
    if (!forceClearCache) {
      const cachedData = await getCachedData(cacheKey);
      if (cachedData) return cachedData;
    } else {
      // If force clear is enabled, remove this specific cache entry
      sessionStorage.removeItem(`cache-${cacheKey}`);
      sessionStorage.removeItem(`cache-${cacheKey}`);
    }

    const cachedData = await getCachedData(cacheKey);
    if (cachedData) return cachedData;

    const response = await fetch(CONTENT_ENDPOINTS.BUCKET_CONTENT, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ bucket: bucketName })
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch ${bucketName} content: ${response.status}`);
    }

    const result = await response.json();
    const transformedData = result.filesMetadata.map((file, index) => ({
      id: index + 1,
      name: file.Key,
      size: file.Size,
      lastModified: file.LastModified
    }));

    // Cache the result
    await setCachedData(cacheKey, transformedData);

    return transformedData;
  } catch (error) {
    console.error(`Error fetching ${bucketName} content:`, error);
    throw error;
  }
};

// Generic file fetcher
export const fetchFileContent = async (bucketName, fileName, token) => {
  try {
    if (!token) {
      console.warn('No token provided to fetchFileContent');
      // Try to get from session storage as fallback
      const credentials = getUserCredentials();
      if (!credentials) {
        throw new Error('No authentication information found');
      }
      token = credentials.token;
    }

    const response = await fetch(STREAM_ENDPOINTS.FILE, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        bucket: bucketName,
        fileName
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch file: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let fileContent = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      fileContent += decoder.decode(value, { stream: true });
    }

    return fileContent;
  } catch (error) {
    console.error('Error fetching file:', error);
    throw error;
  }
};

// Generic file deletion
export const deleteFile = async (bucketName, fileName, token) => {
  try {
    let credentials;
    if (!token) {
      console.warn('No token provided to fetchFileContent');
      // Try to get from session storage as fallback
      credentials = getUserCredentials();
      if (!credentials) {
        throw new Error('No authentication information found');
      }
      token = credentials.token;
    }

    const response = await fetch(DELETE_ENDPOINTS.FILE, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        bucket: bucketName,
        fileName: fileName
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to delete file: ${response.status}`);
    }

    // Clear the cache after a successful delete
    await clearBucketContentCache();

    return true;
  } catch (error) {
    console.error('Error deleting file:', error);
    throw error;
  }
};

// Check payment status
export const checkPaymentStatus = async (token) => {
  try {
    // First try to get from stored user data (faster)
    const userDetails = sessionStorage.getItem('userDetails');
    if (userDetails) {
      const user = JSON.parse(userDetails);
      if (user.hasOwnProperty('isPaymentOverdue')) {
        return {
          isOverdue: user.isPaymentOverdue,
          dataDeletionDate: user.dataDeletionDate || null
        };
      }
    }

    // Fallback to API call if needed
    let credentials = getUserCredentials();
    if (!credentials || !credentials.token || !credentials.email) {
      throw new Error('No authentication information found');
    }

    const response = await fetch(USER_ENDPOINTS.ACCOUNT_DETAILS, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${credentials.token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email: credentials.email })
    });

    if (!response.ok) {
      throw new Error(`Failed to check payment status: ${response.status}`);
    }

    const result = await response.json();
    return {
      isOverdue: result.isOverdue || false,
      dataDeletionDate: result.dataDeletionDate || null
    };
  } catch (error) {
    console.error('Error checking payment status:', error);
    return null; // Let calling code handle this appropriately
  }
};

// Check storage limits
export const checkStorageLimits = async (newFileSize = 0, token) => {
  try {
    let credentials;
    if (!token) {
      console.warn('No token provided to fetchFileContent');
      // Try to get from session storage as fallback
      credentials = getUserCredentials();
      if (!credentials) {
        throw new Error('No authentication information found');
      }
      token = credentials.token;
    }

    let subscriptionType;

    const accountDetails = JSON.parse(sessionStorage.getItem('accountDetails'));

    if (accountDetails && accountDetails.payment && accountDetails.payment.SubscriptionType) {
      subscriptionType = accountDetails.payment.SubscriptionType;
    } else {
      const userDetails = JSON.parse(sessionStorage.getItem('userDetails'));
      subscriptionType = userDetails?.SubscriptionType || 'Unknown';
    }

    const storageLimits = {
      '3': 50 * 1024 * 1024, // 50MB
      '2': 5 * 1024 * 1024 * 1024, // 5GB
      '1': 1 * 1024 * 1024 * 1024 // 1GB
    };

    const maxSize = storageLimits[subscriptionType] || storageLimits['1'];

    const response = await fetch(CONTENT_ENDPOINTS.ALL_BUCKETS_SIZE, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch storage information');
    }

    const data = await response.json();

    const orderedBuckets = [
      'files', 'passwords', 'bankcards',
      'inheritance', 'blockchain',
      'thumbnails', 'user'
    ];

    const filteredBuckets = orderedBuckets
      .map(bucketName =>
        data.bucketSizes.find(bucket => bucket.bucket === bucketName)
      )
      .filter(bucket => bucket !== undefined);

    const filteredTotalSize = filteredBuckets.reduce(
      (total, bucket) => total + bucket.size, 0
    );

    const newTotalSize = filteredTotalSize + newFileSize;

    return {
      withinLimits: newTotalSize <= maxSize,
      currentSize: filteredTotalSize,
      maxSize,
      remainingSpace: Math.max(0, maxSize - filteredTotalSize),
      subscriptionType
    };
  } catch (error) {
    console.error('Error checking storage limits:', error);
    throw error;
  }
};

// Helper function to remove .txt extension
export const removeTxtExtension = (name) => {
  if (!name) return '';
  const nameStr = String(name);
  if (nameStr.endsWith && nameStr.endsWith('.txt')) {
    return nameStr.slice(0, -4);
  }
  return nameStr;
};

// Helper function to add .txt extension if not present
export const ensureTxtExtension = (name) => {
  if (!name) return '';
  const nameStr = String(name);
  if (nameStr.endsWith && nameStr.endsWith('.txt')) {
    return nameStr;
  }
  return nameStr + '.txt';
};

// Format file size
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';

  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));

  return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Format dates specifically for mobile file listings
 * @param {string} isoDateString - ISO date string to format
 * @returns {string} Formatted date string
 */
export const formatItemDate = (isoDateString) => {
  if (!isoDateString) return '';

  try {
    const date = new Date(isoDateString);
    if (isNaN(date.getTime())) return '';

    // Get the device width
    const isMobile = window.innerWidth <= 768;

    if (isMobile) {
      // Very compact mobile format: DD/MM/YY (no time)
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      const year = date.getFullYear().toString().slice(2); // Get last 2 digits

      return `${day}/${month}/${year}`;
    } else {
      // Desktop format
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    }
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

/**
 * Formats an ISO date string into a readable format
 * @param {string} isoDateString - ISO date string to format
 * @param {boolean} includeTime - Whether to include the time (default: true)
 * @returns {string} Formatted date string
 */
export const formatDateTime = (isoDateString, includeTime = true) => {
  if (!isoDateString) return '';

  try {
    const date = new Date(isoDateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return '';
    }

    // Date formatting options
    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };

    // Add time options if requested
    if (includeTime) {
      options.hour = '2-digit';
      options.minute = '2-digit';
    }

    return date.toLocaleDateString('en-US', options);
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

export const isValidS3FileName = (filename) => {
  if (!filename || typeof filename !== 'string') {
    return false;
  }

  // S3-safe characters: alphanumeric, hyphens, underscores, periods, forward slashes, parentheses
  // Avoiding spaces and special characters that can cause issues
  const validS3Regex = /^[a-zA-Z0-9._\-()]+$/;
  
  // Additional checks
  const isValidLength = filename.length > 0 && filename.length <= 1024; // S3 key length limit
  const hasValidFormat = validS3Regex.test(filename);
  
  return isValidLength && hasValidFormat;
};

/**
 * Sanitizes a filename to be S3-safe by replacing invalid characters
 * @param {string} filename - The filename to sanitize
 * @param {string} replacement - Character to replace invalid chars with (default: '-')
 * @returns {string} - Sanitized filename
 */
export const sanitizeS3FileName = (filename, replacement = '-') => {
  if (!filename || typeof filename !== 'string') {
    return '';
  }

  let sanitized = filename
    // Replace invalid characters with replacement
    .replace(/[^a-zA-Z0-9._\-()]/g, replacement)
    // Remove consecutive replacement characters
    .replace(new RegExp(`\\${replacement}+`, 'g'), replacement)
    // Remove leading/trailing replacement characters
    .replace(new RegExp(`^\\${replacement}|\\${replacement}$`, 'g'), '');

  // Ensure it's not empty and not too long
  if (!sanitized) {
    sanitized = 'file';
  }
  
  if (sanitized.length > 1024) {
    sanitized = sanitized.substring(0, 1024);
  }

  return sanitized;
};

/**
 * Auto-detects and fixes S3 filename issues
 * @param {string} filename - The filename to check/fix
 * @returns {string} - S3-safe filename (original if already valid, sanitized if not)
 */
export const ensureS3SafeFileName = (filename) => {
  if (isValidS3FileName(filename)) {
    return filename; // Already valid, return as-is
  }
  
  const sanitized = sanitizeS3FileName(filename);
  return sanitized;
};